import { CheckSquare } from 'lucide-react'
import { Link, router } from '@inertiajs/react'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table'
import { t } from '@/shared/lib/i18n'
import { formatTenantDateTime } from '@/shared/lib/tenantDateTime'
import { complianceDetailPath } from '@/shared/lib/routes'
import ReviewTypeBadge from './ReviewTypeBadge'
import type { WebComplianceReviewList } from '@/shared/types/web'

interface Props {
  records: WebComplianceReviewList[]
  tenantTimeFormat: number | null | undefined
}

// REBUILD-492: show em dash when serial is missing; `?? '—'` misses empty/whitespace strings.
const EM_DASH = '—'
const displaySerial = (serialNumber: string | null | undefined): string =>
  serialNumber?.trim() ? serialNumber : EM_DASH

/** Returns a short TZ abbreviation for an IANA zone name, e.g. "America/New_York" → "EST". */
const timeZoneAbbreviation = (ianaZone: string | null | undefined): string => {
  if (!ianaZone) return ''
  try {
    const formatted = new Intl.DateTimeFormat('en-US', {
      timeZone: ianaZone,
      timeZoneName: 'short',
    })
      .formatToParts(new Date())
      .find((part) => part.type === 'timeZoneName')
    return formatted?.value ?? ''
  } catch {
    return ''
  }
}

const openComplianceDetail = (record: WebComplianceReviewList) => {
  router.visit(complianceDetailPath({ id: record.id }))
}

const ComplianceTable = ({ records, tenantTimeFormat }: Props) => {
  return (
    <>
      {/* Mobile: card list */}
      <div className="space-y-2 lg:hidden">
        {records.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-16 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
              <CheckSquare className="h-5 w-5 text-muted-foreground" />
            </span>
            <p className="text-sm text-muted-foreground">{t('compliance.dashboard.noRecords')}</p>
          </div>
        ) : (
          records.map((record) => (
            <div
              key={record.id}
              className="cursor-pointer rounded-lg border bg-card shadow-sm transition-colors hover:bg-muted/40"
              onClick={() => openComplianceDetail(record)}
            >
              <div className="flex items-start gap-3 p-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-start justify-between gap-1">
                    <span className="truncate font-medium text-primary">
                      {record.instrumentLabel}
                    </span>
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {record.missedTime
                        ? `${formatTenantDateTime(record.missedTime, tenantTimeFormat)} ${timeZoneAbbreviation(record.timeZoneName)}`
                        : record.date}
                    </span>
                  </div>

                  {/* REBUILD-492: match the desktop S/N rule — a whitespace-only serial counts as
                      missing, so the mobile card omits the S/N line rather than showing blank. */}
                  {record.serialNumber?.trim() && (
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {t('compliance.dashboard.serialNumber')}: {record.serialNumber}
                    </p>
                  )}
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {t('compliance.dashboard.table.ellId')}: {record.instrumentId}
                  </p>

                  {record.taskName && (
                    <p className="mt-0.5 text-sm text-muted-foreground">{record.taskName}</p>
                  )}

                  <p className="mt-0.5 text-sm text-muted-foreground">
                    {[record.departmentName, record.unitName, record.siteName]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>

                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <ReviewTypeBadge
                      code={record.reviewStatusCode ?? null}
                      label={record.resolutionLabel ?? null}
                    />
                    <span className="text-xs text-muted-foreground">
                      {record.reviewStatusLabel ?? EM_DASH}
                    </span>
                  </div>
                  {record.confirmedByName && (
                    <p className="mt-1 text-xs text-muted-foreground">{record.confirmedByName}</p>
                  )}
                  {record.confirmedAtLocal && (
                    <p className="text-xs text-muted-foreground">
                      {`${formatTenantDateTime(record.confirmedAtLocal, tenantTimeFormat)} ${record.confirmedAtZoneAbbreviation ?? ''}`.trim()}
                    </p>
                  )}
                  <div className="mt-1">
                    <Link
                      href={record.viewMaintenanceLogsHref}
                      className="text-xs text-primary underline-offset-4 hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {t('compliance.dashboard.viewMaintenanceLogs')}
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Desktop: table */}
      <div className="hidden overflow-x-auto rounded-md border lg:block [&_td]:whitespace-nowrap [&_th]:whitespace-nowrap">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('compliance.dashboard.table.missedDate')}</TableHead>
              <TableHead>{t('compliance.dashboard.table.instrument')}</TableHead>
              <TableHead>{t('compliance.dashboard.table.serialNumber')}</TableHead>
              <TableHead>{t('compliance.dashboard.table.task')}</TableHead>
              <TableHead>{t('compliance.dashboard.table.department')}</TableHead>
              <TableHead>{t('compliance.dashboard.table.site')}</TableHead>
              <TableHead>{t('compliance.dashboard.table.frequency')}</TableHead>
              <TableHead>{t('compliance.dashboard.table.status')}</TableHead>
              <TableHead>{t('compliance.dashboard.table.resolutionType')}</TableHead>
              <TableHead>{t('compliance.dashboard.table.confirmedBy')}</TableHead>
              <TableHead>{t('compliance.dashboard.table.confirmedAt')}</TableHead>
              <TableHead>{t('compliance.dashboard.table.maintenanceLog')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {records.length === 0 ? (
              <TableRow>
                <TableCell colSpan={12} className="py-16 text-center">
                  <div className="flex flex-col items-center gap-3">
                    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                      <CheckSquare className="h-5 w-5 text-muted-foreground" />
                    </span>
                    <p className="text-sm text-muted-foreground">
                      {t('compliance.dashboard.noRecords')}
                    </p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              records.map((record) => (
                <TableRow
                  key={record.id}
                  className="cursor-pointer"
                  onClick={() => openComplianceDetail(record)}
                >
                  <TableCell>
                    {record.missedTime
                      ? `${formatTenantDateTime(record.missedTime, tenantTimeFormat)} ${timeZoneAbbreviation(record.timeZoneName)}`
                      : record.date}
                  </TableCell>
                  <TableCell className="font-medium text-primary">
                    {record.instrumentLabel}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {displaySerial(record.serialNumber)}
                  </TableCell>
                  <TableCell>{record.taskName ?? '—'}</TableCell>
                  <TableCell>
                    {record.departmentName}
                    {record.unitName ? ` / ${record.unitName}` : ''}
                  </TableCell>
                  <TableCell>{record.siteName ?? '—'}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {record.intervalTypeDescription ?? '—'}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {record.reviewStatusLabel ?? EM_DASH}
                  </TableCell>
                  <TableCell>
                    <ReviewTypeBadge
                      code={record.reviewStatusCode ?? null}
                      label={record.resolutionLabel ?? null}
                    />
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {record.confirmedByName ?? '—'}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {record.confirmedAtLocal
                      ? `${formatTenantDateTime(record.confirmedAtLocal, tenantTimeFormat)} ${record.confirmedAtZoneAbbreviation ?? ''}`.trim()
                      : '—'}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Link
                      href={record.viewMaintenanceLogsHref}
                      className="text-sm text-primary underline-offset-4 hover:underline"
                    >
                      {t('compliance.dashboard.table.maintenanceLog')}
                    </Link>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </>
  )
}

export default ComplianceTable
